/**
 * Monetization & Revenue Showcase Page
 * Deep dive into revenue system, IAP, and payout features
 * Solis Games - Revenue System Phase
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const MonetizationShowcase = () => {
  const [activeDemo, setActiveDemo] = useState('breakdown');

  // Sample revenue data
  const sampleRevenueBreakdown = [
    { game: 'Pixel Quest', iap: 1250, ads: 380, total: 1630 },
    { game: 'Space Fighter', iap: 890, ads: 520, total: 1410 },
    { game: 'Racing Legends', iap: 2100, ads: 680, total: 2780 },
  ];

  const samplePayoutHistory = [
    { date: '2025-01-15', amount: 8420.50, status: 'completed' },
    { date: '2024-12-15', amount: 7650.25, status: 'completed' },
    { date: '2024-11-15', amount: 9120.75, status: 'completed' },
  ];

  const revenueFeatures = [
    {
      icon: 'DollarSign',
      title: 'Variable Pricing IAP',
      description: 'Set any price for your in-app purchases. No fixed tiers, no restrictions.',
      benefit: 'Maximize revenue with optimal pricing'
    },
    {
      icon: 'Percent',
      title: '20% Platform Fee',
      description: 'Keep 80% of ALL revenue - both IAP and ads. One unified, fair fee structure.',
      benefit: 'Lower than Steam (30%), Epic (12% + restrictions)'
    },
    {
      icon: 'Calendar',
      title: 'Automated Monthly Payouts',
      description: 'Stripe Connect handles everything. Set up once, get paid automatically every month.',
      benefit: 'No manual invoicing or payment delays'
    },
    {
      icon: 'BarChart3',
      title: 'Per-Game Revenue Tracking',
      description: 'See exactly how much each game earns from IAP vs ads. Complete transparency.',
      benefit: 'Optimize your monetization strategy'
    }
  ];

  const comparisonData = [
    {
      name: 'Solis Games',
      iapFee: '20%',
      adFee: '20%',
      minPayout: '$50',
      payoutFreq: 'Monthly (auto)',
      variablePricing: true,
      perGameBreakdown: true,
      stripeConnect: true,
      highlight: true
    },
    {
      name: 'Steam',
      iapFee: '30%',
      adFee: 'No ads',
      minPayout: '$100',
      payoutFreq: 'Monthly (manual)',
      variablePricing: true,
      perGameBreakdown: false,
      stripeConnect: false,
      highlight: false
    },
    {
      name: 'CrazyGames',
      iapFee: 'Not supported',
      adFee: '30%',
      minPayout: '$200',
      payoutFreq: 'Manual request',
      variablePricing: false,
      perGameBreakdown: false,
      stripeConnect: false,
      highlight: false
    },
    {
      name: 'Poki',
      iapFee: 'Not supported',
      adFee: '30%',
      minPayout: '$250',
      payoutFreq: 'Net 60 days',
      variablePricing: false,
      perGameBreakdown: false,
      stripeConnect: false,
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Monetization & Revenue System - Fair Payouts | Solis Games</title>
        <meta
          name="description"
          content="The fairest revenue sharing in gaming. 20% unified platform fee, automated payouts via Stripe Connect, variable pricing IAP, and complete revenue transparency. Earn more with Solis Games."
        />
      </Helmet>

      <Header />

      <main className="pt-16 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-green-500/5 via-emerald-500/5 to-background py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-6">
                <Icon name="TrendingUp" size={18} className="text-green-500" />
                <span className="text-sm font-semibold text-green-500">EARN MORE, KEEP MORE</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
                Revenue System Built for Developers
              </h1>

              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                <span className="text-foreground font-semibold">20% unified fee</span> across IAP and ads.
                <span className="text-foreground font-semibold"> Automated payouts</span> via Stripe Connect.
                <span className="text-foreground font-semibold"> Complete transparency</span> on every dollar earned.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/user-registration"
                  className="inline-flex items-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Rocket" size={20} className="mr-2" />
                  Start Earning
                </Link>
                <Link
                  to="#comparison"
                  className="inline-flex items-center px-8 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                >
                  <Icon name="Calculator" size={20} className="mr-2" />
                  Compare Revenue
                </Link>
              </div>

              {/* Revenue Calculator Preview */}
              <div className="mt-12 p-6 bg-card border border-border rounded-xl text-left">
                <div className="text-sm text-muted-foreground mb-4 text-center">Example: You earn $10,000</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="text-3xl font-bold text-green-600">$8,000</div>
                    <div className="text-sm text-muted-foreground mt-1">You Keep (80%)</div>
                    <div className="text-xs text-green-600 font-semibold mt-2">Solis Games</div>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-3xl font-bold text-foreground">$7,000</div>
                    <div className="text-sm text-muted-foreground mt-1">You Keep (70%)</div>
                    <div className="text-xs text-muted-foreground font-semibold mt-2">Steam</div>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-3xl font-bold text-foreground">$7,000</div>
                    <div className="text-sm text-muted-foreground mt-1">You Keep (70%)</div>
                    <div className="text-xs text-muted-foreground font-semibold mt-2">CrazyGames</div>
                  </div>
                </div>
                <div className="text-center mt-4 text-sm text-green-600 font-semibold">
                  You earn <span className="text-2xl">${1000}</span> more on Solis Games
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-foreground mb-4">
                  Everything You Need to Monetize
                </h2>
                <p className="text-xl text-muted-foreground">
                  From variable pricing to automated payouts - we've thought of everything
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {revenueFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="p-6 bg-card border border-border rounded-xl hover:border-primary/50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={feature.icon} size={24} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground mb-3">
                      {feature.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Icon name="CheckCircle2" size={16} />
                      <span className="font-medium">{feature.benefit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Demos */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-foreground mb-4">
                  See Your Revenue Dashboard
                </h2>
                <p className="text-xl text-muted-foreground">
                  Exactly what you'll see when you log in
                </p>
              </div>

              {/* Demo Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto">
                {[
                  { id: 'breakdown', label: 'Revenue Breakdown', icon: 'BarChart3' },
                  { id: 'products', label: 'IAP Products', icon: 'ShoppingCart' },
                  { id: 'payouts', label: 'Payout History', icon: 'CreditCard' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveDemo(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      activeDemo === tab.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Demo Content */}
              <div className="bg-card border border-border rounded-xl p-6">
                {activeDemo === 'breakdown' && (
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">Revenue by Game</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Per-game breakdown of IAP and ad revenue (your 80% share)
                    </p>
                    <div className="space-y-4">
                      {sampleRevenueBreakdown.map((game, i) => (
                        <div key={i} className="p-4 bg-muted/30 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-foreground">{game.game}</span>
                            <span className="text-2xl font-bold text-green-600">${game.total.toFixed(2)}</span>
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground">
                            <span>IAP: ${game.iap.toFixed(2)}</span>
                            <span>•</span>
                            <span>Ads: ${game.ads.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeDemo === 'products' && (
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">In-App Purchase Products</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Configure purchasable items (you keep 80% of revenue)
                    </p>
                    <div className="space-y-4">
                      <div className="p-4 border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">100 Gems Pack</span>
                          <span className="text-lg font-bold text-green-600">$4.99</span>
                        </div>
                        <div className="text-sm text-muted-foreground mb-3">
                          Get 100 premium gems to unlock special items
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>You earn: <strong className="text-green-600">$3.99</strong></span>
                          <span>Platform fee (20%): $1.00</span>
                        </div>
                      </div>
                      <div className="p-4 border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">VIP Pass</span>
                          <span className="text-lg font-bold text-green-600">$9.99</span>
                        </div>
                        <div className="text-sm text-muted-foreground mb-3">
                          Monthly VIP subscription with exclusive perks
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>You earn: <strong className="text-green-600">$7.99</strong></span>
                          <span>Platform fee (20%): $2.00</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeDemo === 'payouts' && (
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">Payout History</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Automated monthly payouts via Stripe Connect
                    </p>
                    <div className="space-y-4">
                      {samplePayoutHistory.map((payout, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                          <div>
                            <div className="font-semibold text-foreground">${payout.amount.toFixed(2)}</div>
                            <div className="text-sm text-muted-foreground">{payout.date}</div>
                          </div>
                          <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-sm">
                            <Icon name="CheckCircle2" size={14} />
                            <span>Completed</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Info" size={16} className="text-primary" />
                        <span className="text-sm font-semibold text-primary">Next Payout</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Your next payout of <strong>$5,234.56</strong> will be processed on <strong>February 1, 2025</strong> via Stripe.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Platform Comparison */}
        <section id="comparison" className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-foreground mb-4">
                  How Solis Games Compares
                </h2>
                <p className="text-xl text-muted-foreground">
                  We offer the fairest revenue share and best payout system in the industry
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border border-border rounded-xl overflow-hidden">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Platform</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">IAP Fee</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Ad Fee</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Min Payout</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Payout Frequency</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-foreground">Stripe Connect</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {comparisonData.map((platform, i) => (
                      <tr
                        key={i}
                        className={platform.highlight ? 'bg-green-500/5' : 'bg-card'}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">{platform.name}</span>
                            {platform.highlight && (
                              <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">Best Value</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={platform.highlight ? 'font-bold text-green-600' : 'text-foreground'}>
                            {platform.iapFee}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={platform.highlight ? 'font-bold text-green-600' : 'text-foreground'}>
                            {platform.adFee}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-foreground">{platform.minPayout}</td>
                        <td className="px-6 py-4 text-foreground">{platform.payoutFreq}</td>
                        <td className="px-6 py-4 text-center">
                          {platform.stripeConnect ? (
                            <Icon name="CheckCircle2" size={20} className="text-green-600 mx-auto" />
                          ) : (
                            <Icon name="XCircle" size={20} className="text-red-500 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-foreground mb-4">
                  Getting Paid is Simple
                </h2>
                <p className="text-xl text-muted-foreground">
                  Three steps to start earning
                </p>
              </div>

              <div className="space-y-8">
                {[
                  {
                    step: '1',
                    title: 'Set Up Payment Account',
                    description: 'Connect your bank account via Stripe Connect. Takes 2-3 minutes, completely secure.',
                    icon: 'CreditCard'
                  },
                  {
                    step: '2',
                    title: 'Configure Monetization',
                    description: 'Create IAP products with any price you want. Enable ads if desired. Mix both revenue streams.',
                    icon: 'Settings'
                  },
                  {
                    step: '3',
                    title: 'Get Paid Automatically',
                    description: 'Earn revenue from your games. Get paid automatically every month. Track everything in real-time.',
                    icon: 'TrendingUp'
                  }
                ].map((step, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-2xl font-bold text-primary-foreground">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-foreground mb-2">{step.title}</h3>
                      <p className="text-lg text-muted-foreground">{step.description}</p>
                    </div>
                    <div className="flex-shrink-0 hidden md:block">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={step.icon} size={24} className="text-primary" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Ready to Earn More?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join game developers who are earning 10-15% more on Solis Games
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/user-registration"
                  className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Rocket" size={24} className="mr-2" />
                  Create Studio Account
                </Link>
                <Link
                  to="/developers"
                  className="inline-flex items-center px-8 py-4 border border-border text-foreground rounded-lg font-semibold text-lg hover:bg-muted transition-colors"
                >
                  <Icon name="Book" size={24} className="mr-2" />
                  View Documentation
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MonetizationShowcase;
